import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from '../types';
import { AIChatApprovalCard } from './AIChatApprovalCard';

/** "HH:MM" for messages created today, "DD/Mon HH:MM" otherwise. */
function formatMessageTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  if (isToday) return time;

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  return `${day}/${month} ${time}`;
}

interface Props {
  message: ChatMessage;
  approveToolCall: (messageId: number, toolUseId: string) => Promise<void>;
  rejectToolCall: (messageId: number, toolUseId: string) => Promise<void>;
  decideToolCall: (messageId: number, toolUseId: string, decision: 'approved' | 'rejected') => Promise<void>;
}

export function AIChatMessageBubble({ message, approveToolCall, rejectToolCall, decideToolCall }: Props) {
  const isUser = message.role === 'USER';
  const isPending = message.status === 'STREAMING' || message.status === 'PENDING';
  const pendingApprovals = (message.toolCalls ?? []).filter((t) => t.requiresApproval);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      <Box
        sx={{
          maxWidth: '88%',
          minWidth: 0,
          px: 1.5,
          py: 1,
          borderRadius: 'var(--radius-xl)',
          bgcolor: isUser ? 'primary.main' : 'var(--color-surface-2)',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          border: isUser ? 'none' : '1px solid var(--color-line)',
        }}
      >
        {message.content ? (
          <Box
            sx={{
              fontSize: 13,
              '& p': { m: 0, mb: 0.75, '&:last-child': { mb: 0 } },
              '& pre': { overflowX: 'auto', p: 1, bgcolor: 'action.selected', borderRadius: 1, fontSize: 11.5 },
              '& code': { fontSize: 11.5 },
              '& ul, & ol': { m: 0, mb: 0.75, pl: 2.5 },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
          </Box>
        ) : isPending ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
            <CircularProgress size={12} thickness={5} />
            <Typography variant="caption" color="text.secondary">Thinking…</Typography>
          </Stack>
        ) : null}

        {message.status === 'FAILED' && (
          <Chip
            label={message.error || 'Something went wrong'}
            size="small"
            color="error"
            variant="outlined"
            sx={{ mt: 0.75, maxWidth: '100%' }}
          />
        )}
        {message.status === 'CANCELLED' && !message.content && (
          <Chip label="Stopped" size="small" variant="outlined" sx={{ mt: 0.5 }} />
        )}

        {message.citations && message.citations.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, rowGap: 0.5 }}>
            {message.citations.map((c) => (
              <Chip
                key={`${c.documentId}-${c.sectionId ?? ''}`}
                icon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
                label={c.title}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            ))}
          </Stack>
        )}
      </Box>

      {pendingApprovals.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 0.75, width: '100%', maxWidth: '88%' }}>
          {pendingApprovals.map((toolCall) => (
            <AIChatApprovalCard
              key={toolCall.toolUseId}
              messageId={message.id}
              toolCall={toolCall}
              approveToolCall={approveToolCall}
              rejectToolCall={rejectToolCall}
              decideToolCall={decideToolCall}
            />
          ))}
        </Stack>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10.5, mt: 0.25, px: 0.5 }}
      >
        {formatMessageTime(message.createdAt)}
      </Typography>
    </Box>
  );
}
